import { useCallback, useMemo, useSyncExternalStore } from 'react';

/** The breakpoint the desktop layouts switch at, from the design handoff. */
const DESKTOP_QUERY = '(min-width: 900px)';

/**
 * Subscribed rather than mirrored into state. The obvious version — read once,
 * then setState from an effect — renders the phone layout for one frame before
 * correcting itself, which on a desktop load is a visible flash of the wrong
 * board.
 */
export function useMediaQuery(query: string): boolean {
  const list = useMemo(
    () => (typeof window.matchMedia === 'function' ? window.matchMedia(query) : null),
    [query],
  );

  const subscribe = useCallback(
    (onChange: () => void) => {
      if (!list) return () => {};
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    },
    [list],
  );

  return useSyncExternalStore(
    subscribe,
    () => list?.matches ?? false,
    // Server-rendered HTML does not exist here, but the third argument is also
    // the fallback when matchMedia is missing, as it is in a bare test env.
    () => false,
  );
}

export function useIsDesktop(): boolean {
  return useMediaQuery(DESKTOP_QUERY);
}
